import {
	ReactNode,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import {
	AccordionTitle,
	ChevronDownIcon,
	Css,
	ITheme,
	Markdown,
	StyledAccordionTitle,
	themed,
} from '../../../ui';

export interface IAccordionItemProps {
	title: string | ReactNode;
	subtitle?: ReactNode;
	body?: string;
	isOpen?: boolean;
	className?: string;
	icon?: ReactNode;
	showIsActive?: boolean;
	reverseIcon?: ReactNode;
	backgroundColor?: string;
	children?: ReactNode;
	onToggle?: () => void;
}

export function AccordionItem({
	title,
	subtitle,
	body,
	isOpen,
	className,
	icon,
	showIsActive = false,
	reverseIcon,
	backgroundColor,
	children,
	onToggle,
}: IAccordionItemProps) {
	const accordionTitleId = useId();
	const accordionContentId = useId();
	const ref = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		setIsExpanded((prevExpanded) => isOpen ?? prevExpanded);
	}, [isOpen]);

	const handleToggle = useCallback(() => {
		if (!children && !body) return;
		setIsExpanded(!isExpanded);
		if (onToggle) {
			onToggle();
		}
	}, [children, body, setIsExpanded, isExpanded, onToggle]);

	return (
		<AccordionItemStyled
			className={className}
			backgroundColor={backgroundColor}
		>
			<AccordionTitle
				onClick={handleToggle}
				isExpanded={isExpanded}
				showIsActive={isExpanded && showIsActive}
				title={title}
				icon={icon}
				reverseIcon={
					reverseIcon || (
						<ChevronDownIcon aria-label={isExpanded ? 'Collapse' : 'Expand'} />
					)
				}
				aria-controls={accordionContentId}
				id={accordionTitleId}
				aria-expanded={isExpanded}
			/>
			{subtitle && <div>{subtitle}</div>}

			<StyledAccordionWrapper
				id={accordionContentId}
				ref={ref}
				isExpanded={isExpanded}
				role="region"
				contentHeight={ref.current?.scrollHeight || 0}
				aria-labelledby={accordionTitleId}
			>
				<StyledContentWrap>
					{children || <Markdown source={body as string} />}
				</StyledContentWrap>
			</StyledAccordionWrapper>
		</AccordionItemStyled>
	);
}

const AccordionItemStyled = styled.div<{ backgroundColor?: string }, ITheme>`
	border-bottom: ${themed(({ border }) => border.default)};
	${({ backgroundColor }) =>
		backgroundColor &&
		css`
			background-color: ${backgroundColor};
		`}

	:last-child {
		border-bottom: none;
	}

	svg {
		path {
			fill: ${themed(({ accordion }) => accordion.icon.color)};
		}
	}

	${StyledAccordionTitle} {
		padding: ${themed(({ accordion }) => accordion.title.padding)};
		font-weight: 700;
		text-align: left;
		font-size: ${Css.pxToRem(16)};
	}
`;

export const StyledAccordionWrapper = styled.div<{
	isExpanded: boolean;
	contentHeight?: number;
}>`
	max-height: ${({ isExpanded, contentHeight }) =>
		isExpanded ? `${contentHeight}px` : '0'};
	transition: max-height 0.5s cubic-bezier(0.65, 0, 0.35, 1);
	overflow: hidden;
`;

const StyledContentWrap = styled.div`
	margin: 0 0 ${Css.pxToRem(12)};
	ul,
	ol {
		margin-top: 0;
	}
`;
